import { type FormEvent } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import {
  forgotSchema,
  loginSchema,
  resetSchema,
  twoFactorSchema,
  type LoginStep,
} from '@/utils/loginSchemas'
import type { Role } from '@/types'
import {
  encodeLoginIntentTime,
  getBrowserLocation,
  getDeviceFingerprint,
  getDeviceName,
  getLoginTelemetry,
  isLoginIntentStale,
} from '@/utils/security'

interface LoginFlowHandlerDeps {
  email: string
  password: string
  twoFactorCode: string
  resetToken: string
  newPassword: string
  confirmNewPassword: string
  honeypot: string
  rememberMe: boolean
  loading: boolean
  rateLimit: {
    isLocked: boolean
    getWarningMessage: () => string
    recordFailedAttempt: () => void
    resetAttempts: () => void
  }
  loginIntentTime: string
  executeRecaptcha: (action?: string) => Promise<string | undefined>
  recaptchaEnabled: boolean
  login: (user: import('@/types').User, token?: string) => void
  persistRememberMe: () => void
  redirectAfterAuth: (user: { role: Role }) => void
  setError: (msg: string) => void
  setLoading: (v: boolean) => void
  setStep: (step: LoginStep) => void
  setSuccessMessage: (msg: string) => void
  setResetToken: (v: string) => void
  setNewPassword: (v: string) => void
  setConfirmNewPassword: (v: string) => void
  setPassword: (v: string) => void
}

function extractApiMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { error?: unknown; message?: string } } }
  const apiErr = axiosErr.response?.data?.error
  return (
    typeof apiErr === 'string'
      ? apiErr
      : (apiErr as { message?: string })?.message ||
        axiosErr.response?.data?.message ||
        fallback
  )
}

export function useLoginFlowHandlers(deps: LoginFlowHandlerDeps) {
  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (deps.loading || deps.rateLimit.isLocked) return

    deps.setError('')

    if (deps.rateLimit.isLocked) {
      deps.setError(deps.rateLimit.getWarningMessage())
      return
    }

    const validation = loginSchema.safeParse({ email: deps.email, password: deps.password })
    if (!validation.success) {
      deps.setError(validation.error.issues[0].message)
      return
    }

    deps.setLoading(true)

    try {
      let recaptchaToken: string | undefined
      try {
        recaptchaToken = await deps.executeRecaptcha('login')
      } catch {
        recaptchaToken = undefined
      }
      if (!recaptchaToken && (import.meta.env.PROD || deps.recaptchaEnabled)) {
        deps.setError(
          'Validação de segurança incompleta. Desative bloqueadores, recarregue a página e tente novamente.',
        )
        deps.setLoading(false)
        return
      }

      const browserLocation = await getBrowserLocation()
      const telemetry = getLoginTelemetry()
      const email = deps.email.toLowerCase().trim()

      let loginIntentTime = deps.loginIntentTime
      if (isLoginIntentStale(loginIntentTime)) {
        loginIntentTime = encodeLoginIntentTime()
        // Anti-bot exige ≥300ms entre intent e submit.
        await new Promise((r) => setTimeout(r, 350))
      }

      const payload = {
        email,
        password: deps.password,
        recaptchaToken,
        deviceFingerprint: getDeviceFingerprint(),
        deviceName: getDeviceName(),
        browserLocation,
        loginIntentTime,
        hp_field_v1: deps.honeypot || undefined,
        rememberMe: deps.rememberMe,
        ...telemetry,
      }

      let resp
      try {
        resp = await api.post('/auth/login', payload)
      } catch (firstErr: unknown) {
        const code = (firstErr as { response?: { data?: { error?: { code?: string } } } }).response
          ?.data?.error?.code
        if (code !== 'RECAPTCHA_RETRY') throw firstErr
        const retryToken = await deps.executeRecaptcha('login').catch(() => undefined)
        resp = await api.post('/auth/login', { ...payload, recaptchaToken: retryToken })
      }

      const result = resp.data.data ?? resp.data

      // Senha não fica em memória após o passo de credenciais.
      deps.setPassword('')

      if (result.require2FA || result.requiresTwoFactor) {
        deps.setStep('twoFactor')
        deps.setError('')
        return
      }

      if (result.user) {
        deps.rateLimit.resetAttempts()
        deps.login(result.user, result.token ?? '')
        deps.persistRememberMe()
        deps.redirectAfterAuth(result.user)
      } else {
        deps.setError('Não foi possível concluir o login. Tente novamente.')
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status
      const apiCode = (err as { response?: { data?: { error?: { code?: string } } } }).response
        ?.data?.error?.code
      const isRecaptchaRetry = apiCode === 'RECAPTCHA_RETRY'
      if (!isRecaptchaRetry) deps.rateLimit.recordFailedAttempt()
      const msg =
        isRecaptchaRetry
          ? extractApiMessage(err, 'A verificação de segurança não concluiu. Tente entrar de novo.')
          : status === 429 || status === 423
            ? extractApiMessage(err, 'Muitas tentativas. Aguarde e tente novamente.')
            : status === 401
              ? 'E-mail ou senha inválidos.'
              : extractApiMessage(err, 'Não foi possível fazer login. Tente novamente.')
      deps.setError(msg)
      toast.error(msg)
    } finally {
      deps.setLoading(false)
    }
  }

  const handleTwoFactorSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (deps.loading) return

    deps.setError('')

    const validation = twoFactorSchema.safeParse({
      email: deps.email,
      code: deps.twoFactorCode,
    })
    if (!validation.success) {
      deps.setError(validation.error.issues[0].message)
      return
    }

    deps.setLoading(true)

    try {
      const resp = await api.post('/auth/verify-2fa', {
        email: validation.data.email.toLowerCase().trim(),
        code: validation.data.code,
        deviceFingerprint: getDeviceFingerprint(),
        deviceName: getDeviceName(),
      })

      const result = resp.data.data ?? resp.data

      if (result.user) {
        deps.rateLimit.resetAttempts()
        deps.login(result.user, result.token ?? '')
        deps.persistRememberMe()
        deps.redirectAfterAuth(result.user)
      } else {
        deps.setError('Código inválido ou expirado.')
        deps.rateLimit.recordFailedAttempt()
      }
    } catch (err: unknown) {
      deps.rateLimit.recordFailedAttempt()
      const msg = extractApiMessage(err, 'Erro ao verificar código 2FA.')
      deps.setError(msg)
      toast.error(msg)
    } finally {
      deps.setLoading(false)
    }
  }

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (deps.loading) return

    deps.setError('')

    const validation = forgotSchema.safeParse({ email: deps.email })
    if (!validation.success) {
      deps.setError(validation.error.issues[0].message)
      return
    }

    deps.setLoading(true)

    try {
      await api.post('/auth/forgot-password', {
        email: deps.email.toLowerCase().trim(),
      })

      // Mensagem genérica (não revela se o e-mail existe).
      // Não abre "redefinir senha" aqui: só o link do e-mail (token único de uso único) libera essa etapa.
      deps.setSuccessMessage(
        'Se o e-mail estiver cadastrado, você receberá um link de recuperação. Abra o e-mail e clique no botão para redefinir a senha.',
      )
      deps.setError('')
    } catch {
      deps.setError('Não foi possível processar a solicitação. Tente novamente.')
    } finally {
      deps.setLoading(false)
    }
  }

  const handleResetSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (deps.loading) return

    deps.setError('')

    const validation = resetSchema.safeParse({
      token: deps.resetToken,
      newPassword: deps.newPassword,
      confirmPassword: deps.confirmNewPassword,
    })
    if (!validation.success) {
      deps.setError(validation.error.issues[0].message)
      return
    }

    deps.setLoading(true)

    try {
      await api.post('/auth/reset-password', {
        email: deps.email.toLowerCase().trim(),
        token: deps.resetToken,
        newPassword: deps.newPassword,
      })

      deps.setSuccessMessage('Senha alterada com sucesso! Faça login.')
      deps.setStep('credentials')
      deps.setError('')
      deps.setResetToken('')
      deps.setNewPassword('')
      deps.setConfirmNewPassword('')
    } catch (err: unknown) {
      const msg = extractApiMessage(err, 'Erro ao redefinir senha.')
      deps.setError(msg)
      toast.error(msg)
    } finally {
      deps.setLoading(false)
    }
  }

  return {
    handleLoginSubmit,
    handleTwoFactorSubmit,
    handleForgotSubmit,
    handleResetSubmit,
  }
}
